import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Download, Users, Trash2, ShoppingCart, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LEAD_STATUSES } from "@/lib/leadStatuses";

const AdminLeads = () => {
    const [leads, setLeads] = useState([]);
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkedIds, setCheckedIds] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [abandoned, setAbandoned] = useState([]);
    const [remindingId, setRemindingId] = useState(null);

    const allChecked = leads.length > 0 && leads.every((l) => checkedIds.has(l.id));
    const someChecked = checkedIds.size > 0 && !allChecked;
    const toggleOne = (id) => setCheckedIds((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const toggleAll = () => setCheckedIds(allChecked ? new Set() : new Set(leads.map((l) => l.id)));
    const clearSelection = () => setCheckedIds(new Set());

    const bulkDelete = async () => {
        if (checkedIds.size === 0) return;
        if (!window.confirm(`Delete ${checkedIds.size} selected leads?`)) return;
        setBulkBusy(true);
        const ids = Array.from(checkedIds);
        let ok = 0;
        for (const id of ids) { try { await api.delete(`/admin/leads/${id}`); ok++; } catch {} }
        setLeads((ls) => ls.filter((l) => !checkedIds.has(l.id)));
        clearSelection();
        setBulkBusy(false);
        toast.success(`Deleted ${ok} of ${ids.length} leads`);
    };

    const bulkChangeStatus = async (status) => {
        if (checkedIds.size === 0 || !status) return;
        setBulkBusy(true);
        const ids = Array.from(checkedIds);
        let ok = 0;
        for (const id of ids) { try { await api.patch(`/admin/leads/${id}`, { status }); ok++; } catch {} }
        setLeads((ls) => ls.map((l) => checkedIds.has(l.id) ? { ...l, status } : l));
        setBulkBusy(false);
        toast.success(`Updated ${ok} of ${ids.length} leads → ${status}`);
        clearSelection();
    };

    const load = async () => {
        setLoading(true);
        try {
            const [l, t, a] = await Promise.all([
                api.get("/admin/leads"),
                api.get("/admin/team").catch(() => ({ data: { members: [] } })),
                api.get("/orders/admin/abandoned").catch(() => ({ data: { abandoned: [] } })),
            ]);
            setLeads(l.data.leads || []);
            setTeam(t.data.members || []);
            setAbandoned(a.data.abandoned || []);
        } catch (e) {
            toast.error("Failed to load leads");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const sendReminder = async (row) => {
        setRemindingId(row.id);
        try {
            const { data } = await api.post(`/orders/admin/abandoned/${row.id}/remind`);
            setAbandoned((rows) => rows.map((r) => r.id === row.id ? { ...r, reminders_sent: data.reminders_sent, last_reminder_at: data.last_reminder_at } : r));
            toast.success(data.emailed ? "Reminder email sent" : "Reminder logged (SMTP disabled)");
            if (row.whatsapp_url) window.open(row.whatsapp_url, "_blank", "noopener,noreferrer");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to send");
        } finally {
            setRemindingId(null);
        }
    };

    const setStatus = async (l, status) => {
        try { await api.patch(`/admin/leads/${l.id}`, { status }); setLeads((ls) => ls.map((x) => x.id === l.id ? { ...x, status } : x)); toast.success("Updated"); }
        catch { toast.error("Failed"); }
    };

    const assign = async (l, assigned_to) => {
        try {
            const { data } = await api.post(`/admin/leads/${l.id}/assign`, { assigned_to: assigned_to || null });
            setLeads((ls) => ls.map((x) => x.id === l.id ? { ...x, assigned_to: data.assigned_to } : x));
            toast.success(assigned_to ? "Lead assigned & notified" : "Unassigned");
        } catch { toast.error("Failed"); }
    };

    const deleteLead = async (l) => {
        if (!window.confirm(`Delete lead "${l.name}"?`)) return;
        try {
            await api.delete(`/admin/leads/${l.id}`);
            setLeads((ls) => ls.filter((x) => x.id !== l.id));
            toast.success("Lead deleted");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const exportCsv = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/export`, "_blank");

    return (
        <div className="p-8" data-testid="admin-leads">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Leads</h1>
                    <p className="text-sm text-muted-foreground">{leads.length} captured leads · assign to sales team</p>
                </div>
                <Button onClick={exportCsv} data-testid="admin-export-leads" className="rounded-full"><Download className="w-4 h-4" /> Export CSV</Button>
            </div>

            {/* Abandoned checkouts */}
            <div className="mb-8 rounded-2xl bg-card border overflow-hidden" data-testid="abandoned-section">
                <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between bg-amber-50/60">
                    <div className="flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4 text-amber-700" />
                        <div>
                            <div className="text-sm font-semibold">Abandoned checkouts</div>
                            <div className="text-[11px] text-muted-foreground">Customers who reached checkout but didn't pay — send a reminder to win them back.</div>
                        </div>
                    </div>
                    <div className="text-xs text-muted-foreground" data-testid="abandoned-count">{abandoned.length} pending</div>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs">
                        <tr>
                            <th className="p-3">Order</th>
                            <th className="p-3">Customer</th>
                            <th className="p-3">Cart</th>
                            <th className="p-3">Total</th>
                            <th className="p-3">Placed</th>
                            <th className="p-3">Reminders</th>
                            <th className="p-3 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {abandoned.map((r) => (
                            <tr key={r.id} className="border-t border-border" data-testid={`abandoned-${r.id}`}>
                                <td className="p-3">
                                    <div className="font-mono text-xs">{r.order_number}</div>
                                    <div className="text-[10px] text-muted-foreground">
                                        {r.payment_status === "cod_awaiting_confirmation" ? "COD — awaiting email confirm" : "Online payment — not paid"}
                                    </div>
                                </td>
                                <td className="p-3">
                                    <div className="font-semibold">{r.customer_name || "—"}</div>
                                    <div className="text-[11px] text-muted-foreground">{r.customer_email || r.customer_phone || "—"}</div>
                                </td>
                                <td className="p-3 text-xs text-muted-foreground max-w-[240px] truncate" title={r.items_summary}>{r.items_summary}</td>
                                <td className="p-3 font-semibold">₹{(r.total_inr || 0).toLocaleString()}</td>
                                <td className="p-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                                <td className="p-3 text-xs">
                                    <div>{r.reminders_sent || 0} sent</div>
                                    {r.last_reminder_at && <div className="text-[10px] text-muted-foreground">last {new Date(r.last_reminder_at).toLocaleString()}</div>}
                                </td>
                                <td className="p-3 text-right">
                                    <Button
                                        size="sm"
                                        onClick={() => sendReminder(r)}
                                        disabled={remindingId === r.id}
                                        data-testid={`abandoned-remind-${r.id}`}
                                        className="rounded-full h-8"
                                    >
                                        {remindingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                        Send reminder
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {abandoned.length === 0 && !loading && (
                            <tr><td colSpan={7} className="p-6 text-center text-xs text-muted-foreground italic" data-testid="abandoned-empty">
                                No abandoned checkouts right now. Nice work! ✨
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {checkedIds.size > 0 && (
                <div className="mb-4 rounded-2xl border border-primary/40 bg-primary/5 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="leads-bulk-bar">
                    <div className="text-sm font-semibold" data-testid="leads-bulk-count">{checkedIds.size} selected</div>
                    <select
                        onChange={(e) => { if (e.target.value) { bulkChangeStatus(e.target.value); e.target.value = ""; } }}
                        disabled={bulkBusy}
                        data-testid="leads-bulk-status"
                        className="rounded-lg border px-2 h-9 text-sm bg-white"
                        defaultValue=""
                    >
                        <option value="" disabled>Change status to…</option>
                        {LEAD_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <Button onClick={bulkDelete} disabled={bulkBusy} variant="destructive" data-testid="leads-bulk-delete" className="rounded-full h-9">
                        <Trash2 className="w-4 h-4" /> Delete selected
                    </Button>
                    <button onClick={clearSelection} disabled={bulkBusy} className="text-xs text-muted-foreground underline ml-auto" data-testid="leads-bulk-clear">Clear</button>
                </div>
            )}

            <div className="rounded-2xl bg-card border overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left">
                        <tr>
                            <th className="p-3 w-10">
                                <input
                                    type="checkbox"
                                    checked={allChecked}
                                    ref={(el) => { if (el) el.indeterminate = someChecked; }}
                                    onChange={toggleAll}
                                    data-testid="leads-select-all"
                                    aria-label="Select all leads"
                                />
                            </th>
                            <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3">City / Occasion</th><th className="p-3">Assigned to</th><th className="p-3">Status</th><th className="p-3">Received</th><th className="p-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {leads.map((l) => {
                            const isChecked = checkedIds.has(l.id);
                            return (
                            <tr key={l.id} className={`border-t border-border ${isChecked ? "bg-primary/5" : ""}`} data-testid={`lead-${l.id}`}>
                                <td className="p-3">
                                    <input type="checkbox" checked={isChecked} onChange={() => toggleOne(l.id)} data-testid={`lead-select-${l.id}`} aria-label={`Select lead ${l.name}`} />
                                </td>
                                <td className="p-3 font-semibold">{l.name}</td>
                                <td className="p-3">{l.email}</td>
                                <td className="p-3">{l.phone}</td>
                                <td className="p-3 text-xs text-muted-foreground">{l.city || "—"} · {l.occasion || "—"}</td>
                                <td className="p-3">
                                    <select value={l.assigned_to || ""} onChange={(e) => assign(l, e.target.value)} data-testid={`lead-assign-${l.id}`} className="rounded-lg border px-2 h-8 text-xs">
                                        <option value="">Unassigned</option>
                                        {team.filter((m) => ["sales", "support"].includes(m.role)).map((m) => <option key={m.id} value={m.id}>{m.name} · {m.role}</option>)}
                                    </select>
                                </td>
                                <td className="p-3">
                                    <select value={l.status} onChange={(e) => setStatus(l, e.target.value)} className="rounded-lg border px-2 h-8 text-xs" data-testid={`lead-status-${l.id}`}>
                                        {LEAD_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                </td>
                                <td className="p-3 text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</td>
                                <td className="p-3 text-right">
                                    <button
                                        onClick={() => deleteLead(l)}
                                        data-testid={`lead-delete-${l.id}`}
                                        title="Delete lead"
                                        className="w-8 h-8 rounded-lg hover:bg-destructive/10 text-destructive"
                                    >
                                        <Trash2 className="w-4 h-4 mx-auto" />
                                    </button>
                                </td>
                            </tr>
                            );
                        })}
                        {leads.length === 0 && !loading && <tr><td colSpan={9} className="p-8 text-center text-sm text-muted-foreground"><Users className="w-6 h-6 mx-auto opacity-40 mb-1" />No leads yet.</td></tr>}
                        {loading && <tr><td colSpan={9} className="p-8 text-center text-sm text-muted-foreground">Loading…</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminLeads;
